import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, ChevronRight } from 'lucide-react';
import { fetchMaintenances, type Position } from '@/lib/traccar-api';
import { getLinkedDeviceId, getMaintenanceProgress } from '@/lib/maintenance-status';

interface MaintenanceAlertProps {
  positions: Position[];
}

// Slim bar on the dashboard, shown only when a maintenance reminder is overdue or due soon.
export const MaintenanceAlert = ({ positions }: MaintenanceAlertProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data: maintenances = [] } = useQuery({
    queryKey: ['maintenances'],
    queryFn: fetchMaintenances,
    staleTime: 5 * 60 * 1000,
  });

  const { overdue, dueSoon } = useMemo(() => {
    const positionByDeviceId = new Map(positions.map((position) => [position.deviceId, position]));
    let overdue = 0;
    let dueSoon = 0;
    for (const maintenance of maintenances) {
      const deviceId = getLinkedDeviceId(maintenance);
      const { status } = getMaintenanceProgress(maintenance, deviceId ? positionByDeviceId.get(deviceId) : null);
      if (status === 'overdue') overdue++;
      else if (status === 'dueSoon') dueSoon++;
    }
    return { overdue, dueSoon };
  }, [maintenances, positions]);

  if (!overdue && !dueSoon) return null;

  return (
    <button
      type="button"
      onClick={() => navigate('/maintenance')}
      className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm text-left transition-colors ${
        overdue
          ? 'bg-red-500/10 text-red-700 hover:bg-red-500/15 dark:text-red-400'
          : 'bg-amber-500/10 text-amber-700 hover:bg-amber-500/15 dark:text-amber-400'
      }`}
    >
      <AlertTriangle className="h-4 w-4 shrink-0" />
      <span className="flex-1">{t('dashboard.maintenanceAlert', { overdue, dueSoon })}</span>
      <ChevronRight className="h-4 w-4 shrink-0" />
    </button>
  );
};
